import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store/AppStore';

const QUICK_ACTIONS = [
  { label: 'Yeni Talep', icon: 'ri-file-list-3-line', path: '/teklif-talepleri' },
  { label: 'Yeni Müşteri', icon: 'ri-user-add-line', path: '/musteriler' },
  { label: 'Yeni Randevu', icon: 'ri-calendar-line', path: '/randevu' },
  { label: 'Ödeme Kaydet', icon: 'ri-wallet-3-line', path: '/tahsilatlar' },
];

interface SearchResult {
  type: string;
  label: string;
  sub: string;
  path: string;
}

export default function Topbar({ onMenuClick }: { onMenuClick: () => void }) {
  const [quickOpen, setQuickOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const quickRef = useRef<HTMLDivElement>(null);
  const notifRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { customers, requests, quotes, jobs, appointments, notifications, markNotificationRead, markAllNotificationsRead } =
    useAppStore();

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (quickRef.current && !quickRef.current.contains(e.target as Node)) setQuickOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) setNotifOpen(false);
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) setSearchOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const q = search.trim().toLowerCase();
  const results: SearchResult[] = [];
  if (q) {
    customers
      .filter((c) => c.name.toLowerCase().includes(q) || c.phone.replace(/\s/g, '').includes(q.replace(/\s/g, '')))
      .slice(0, 4)
      .forEach((c) => results.push({ type: 'Müşteri', label: c.name, sub: c.phone, path: '/musteriler' }));
    quotes
      .filter((x) => x.id.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((x) => results.push({ type: 'Teklif', label: x.id, sub: `${x.customerName} · ${x.service}`, path: '/teklifler' }));
    requests
      .filter((r) => r.customerName.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((r) => results.push({ type: 'Talep', label: r.customerName, sub: r.service, path: '/teklif-talepleri' }));
    appointments
      .filter((a) => a.customerName.toLowerCase().includes(q) || a.location.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((a) => results.push({ type: 'Randevu', label: `${a.customerName}`, sub: `${a.location} · ${a.startTime}`, path: '/randevu' }));
    jobs
      .filter((j) => j.customerName.toLowerCase().includes(q) || j.id.toLowerCase().includes(q))
      .slice(0, 3)
      .forEach((j) => results.push({ type: 'İş', label: j.id, sub: `${j.customerName} · ${j.service}`, path: '/aktif-isler' }));
  }

  const unreadCount = notifications.filter((n) => !n.read).length;

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (q) navigate('/musteriler');
    setSearchOpen(false);
  }

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-background-200 bg-white/95 px-4 backdrop-blur md:px-6">
      <button
        type="button"
        onClick={onMenuClick}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-200 text-foreground-600 hover:bg-background-100 lg:hidden"
        aria-label="Menü"
      >
        <i className="ri-menu-line text-lg" />
      </button>

      {/* Search */}
      <div className="relative w-full max-w-md" ref={searchRef}>
        <form onSubmit={handleSearchSubmit}>
          <i className="ri-search-line pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setSearchOpen(true);
            }}
            onFocus={() => setSearchOpen(true)}
            placeholder="Müşteri, telefon veya teklif ara..."
            className="h-10 w-full rounded-lg border border-background-200 bg-background-50 pl-10 pr-4 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary-100"
          />
        </form>

        {searchOpen && q && (
          <div className="absolute left-0 right-0 top-12 z-50 max-h-96 overflow-y-auto rounded-lg border border-background-200 bg-white py-1">
            {results.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-foreground-400">Sonuç bulunamadı.</div>
            ) : (
              results.map((r, i) => (
                <button
                  key={`${r.type}-${i}`}
                  type="button"
                  onClick={() => {
                    setSearchOpen(false);
                    setSearch('');
                    navigate(r.path);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left hover:bg-background-50"
                >
                  <span className="shrink-0 rounded-md bg-background-100 px-2 py-0.5 text-[11px] font-semibold text-foreground-500">
                    {r.type}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-foreground-900">{r.label}</div>
                    <div className="truncate text-xs text-foreground-500">{r.sub}</div>
                  </div>
                  <i className="ri-arrow-right-s-line text-foreground-300" />
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-2">
        {/* Notifications */}
        <div className="relative" ref={notifRef}>
          <button
            type="button"
            onClick={() => setNotifOpen((v) => !v)}
            className="relative flex h-10 w-10 items-center justify-center rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100"
            aria-label="Bildirimler"
          >
            <i className="ri-notification-3-line text-lg" />
            {unreadCount > 0 && (
              <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent-500 px-1 text-[10px] font-bold text-primary-950">
                {unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div className="absolute right-0 top-12 w-80 overflow-hidden rounded-lg border border-background-200 bg-white">
              <div className="flex items-center justify-between border-b border-background-100 px-4 py-3">
                <span className="text-sm font-bold text-foreground-900">Bildirimler</span>
                <button
                  type="button"
                  onClick={markAllNotificationsRead}
                  className="text-xs font-semibold text-primary-600 hover:text-primary-700"
                >
                  Tümünü Okundu İşaretle
                </button>
              </div>
              <ul className="max-h-96 overflow-y-auto divide-y divide-background-100">
                {notifications.map((n) => (
                  <li key={n.id}>
                    <button
                      type="button"
                      onClick={() => {
                        markNotificationRead(n.id);
                        setNotifOpen(false);
                        navigate(n.actionPath);
                      }}
                      className={`flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-background-50 ${n.read ? 'opacity-60' : ''}`}
                    >
                      <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${n.read ? 'bg-background-100 text-foreground-400' : 'bg-accent-100 text-accent-700'}`}>
                        <i className="ri-notification-3-line" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-foreground-900">{n.title}</span>
                          {!n.read && <span className="h-2 w-2 rounded-full bg-accent-500" />}
                        </div>
                        <p className="mt-0.5 text-xs text-foreground-500">{n.description}</p>
                        <div className="mt-0.5 text-[11px] text-foreground-400">{n.time}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="relative" ref={quickRef}>
          <button
            type="button"
            onClick={() => setQuickOpen((v) => !v)}
            className="flex h-10 items-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-4 text-sm font-semibold text-white hover:bg-primary-600"
          >
            <i className="ri-add-line text-base" />
            <span className="hidden sm:inline">Yeni Kayıt</span>
            <span className="sm:hidden">Yeni</span>
            <i className={`ri-arrow-down-s-line text-sm transition-transform ${quickOpen ? 'rotate-180' : ''}`} />
          </button>

          {quickOpen && (
            <div className="absolute right-0 top-12 w-56 overflow-hidden rounded-lg border border-background-200 bg-white py-1">
              {QUICK_ACTIONS.map((action) => (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => {
                    setQuickOpen(false);
                    navigate(action.path);
                  }}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm text-foreground-700 hover:bg-background-50"
                >
                  <i className={`${action.icon} text-base text-foreground-500`} />
                  {action.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </header>
  );
}